import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth, navigateWithHandoff, shouldBypassHandoff } from "@d-m-organization/hub-shared-lib/auth";
import { usePricing } from "@d-m-organization/pricing-ui";
import { LogoMark } from "../components/LogoMark.js";
import { ProductIcon } from "../components/ProductIcon.js";
import { PublicLanding } from "./PublicLanding.js";
import { PRODUCTS, IS_SINGLE_PRODUCT_MODE, SOLE_PRODUCT } from "../config/products.js";
import type { ProductIconType } from "../config/products.js";
import { derivePlanView } from "../lib/derivePlanView.js";
import { formatEuro } from "../lib/formatEuro.js";
import { InlinePlanModal } from "../components/InlinePlanModal.js";
import type { PlanChangeIntent } from "../components/PlanChangeFlow.js";
import { DashboardKpiSection } from "../components/DashboardKpiSection.js";
import { PendingInvitations } from "../components/PendingInvitations.js";
import { DashboardLiveFeed } from "../components/DashboardLiveFeed.js";

// ── Tokens v2 ──────────────────────────────────────────────────────────────
const T = {
  purple: "#351DBD",
  purpleDark: "#1a0f6e",
  purpleLight: "#EDE9FC",
  orange: "#FF861D",
  orangeLight: "#FFF0E2",
  blue: "#349DFF",
  blueLight: "#E5F2FF",
  navy: "#261F59",
  textStrong: "#2b2e44",
  textMuted: "#6b7084",
  textFaint: "#9096a8",
  muted: "#E3EBF6",
  mutedSoft: "#EEF1F7",
  bg: "#f0eef6",
  bgTint: "#F7F6FB",
  white: "#ffffff",
  success: "#22c55e",
  successLight: "#dcfce7",
  successText: "#166534",
  font: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  shadow: {
    sm: "0 1px 3px rgba(53,29,189,0.04), 0 2px 8px rgba(53,29,189,0.04)",
    md: "0 4px 12px rgba(53,29,189,0.08), 0 2px 4px rgba(53,29,189,0.04)",
    lg: "0 16px 40px rgba(53,29,189,0.1), 0 4px 12px rgba(53,29,189,0.06)",
    btn: "0 2px 8px rgba(53,29,189,0.18), 0 1px 2px rgba(53,29,189,0.12)",
  },
} as const;

// ── App data ───────────────────────────────────────────────────────────────
interface AppInfo {
  id: string;
  name: string;
  icon: ProductIconType;
  accent: string;
  tint: string;
  status: "live" | "soon";
  usage?: string;
  eta?: string;
  url?: string;
}

const APPS: AppInfo[] = PRODUCTS.map((p) => ({
  id: p.id,
  name: p.name,
  icon: p.icon,
  accent: p.colors.accent,
  tint: p.colors.accentLight,
  status: p.status === "live" || p.status === "beta" ? "live" as const : "soon" as const,
  usage: p.status === "live" ? "124 scans / 7d" : undefined,
  eta: p.status === "coming-soon" ? "Coming soon" : undefined,
  url: p.url,
}));

// In-hub dashboard routes for live products. Apps listed here open their
// hub dashboard from the "Your apps" rail; others fall back to their
// external app URL. (QR + Planner live only under "Your apps", not Overview.)
const APP_DASHBOARD_PATHS: Record<string, string> = {
  qr: "/qr",
  planner: "/planner",
};

// HD-96: hardcoded FEED removed — live feed now sourced from
// `<DashboardLiveFeed />`, which polls /v1/dashboard/feed every 30 s.

// ── Icon helper ────────────────────────────────────────────────────────────
function Icon({ type, color = "currentColor", size = 20, strokeWidth = 2 }: { type: string; color?: string; size?: number; strokeWidth?: number }) {
  const p = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  switch (type) {
    case "home": return <svg {...p}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2h-4v-7H10v7H6a2 2 0 0 1-2-2z" /></svg>;
    case "plus": return <svg {...p}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>;
    case "arrow": return <svg {...p}><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>;
    case "external": return <svg {...p}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>;
    case "copy": return <svg {...p}><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>;
    case "bell": return <svg {...p}><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>;
    case "settings": return <svg {...p}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.36.11.71.29 1 .51H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>;
    case "search": return <svg {...p}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>;
    case "trend": return <svg {...p}><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></svg>;
    case "help": return <svg {...p}><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>;
    case "check": return <svg {...p}><polyline points="20 6 9 17 4 12" /></svg>;
    case "lock": return <svg {...p}><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>;
    case "eye": return <svg {...p}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>;
    case "mobile": return <svg {...p}><rect x="5" y="2" width="14" height="20" rx="2" /><line x1="12" y1="18" x2="12.01" y2="18" /></svg>;
    case "globe": return <svg {...p}><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>;
    case "qr": return <svg {...p}><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><path d="M14 14h3v3" /><path d="M21 21v.01" /><path d="M21 14v3" /><path d="M14 21h3" /></svg>;
    case "calendar": return <svg {...p}><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>;
    default: return null;
  }
}

// ═════════════════════════════════════════════════════════════════════════════
// ROOT ROUTE HANDLER
// ═════════════════════════════════════════════════════════════════════════════
export function Dashboard() {
  const { isAuthed, isAuthReady, user, userLabel, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ADR-0010 M5c: resume a pending invitation after a signed-out invitee logged
  // in (Cognito Hosted UI always returns to the dashboard). Allow-list to
  // `/invites/...` only — never navigate to an arbitrary stored path.
  useEffect(() => {
    if (!isAuthed) return;
    let path: string | null = null;
    try {
      path = sessionStorage.getItem("gp:pendingInvitePath");
      if (path) sessionStorage.removeItem("gp:pendingInvitePath");
    } catch {
      path = null;
    }
    if (path && /^\/invites\/[^/?#]+(\?.*)?$/.test(path)) {
      navigate(path, { replace: true });
    }
  }, [isAuthed, navigate]);

  if (!isAuthReady) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <LogoMark color={T.purple} size={40} />
          <p style={{ fontSize: 14, color: T.textMuted, marginTop: 16 }}>{t("dashboard.common.loading")}</p>
        </div>
      </div>
    );
  }

  if (!isAuthed) {
    // Single-product mode: the hub root is just an auth gateway — no marketing
    // landing. Send visitors straight to login. Ecosystem mode (2+ live
    // products) keeps the full landing. See config/products.ts.
    return IS_SINGLE_PRODUCT_MODE ? <Navigate to="/login" replace /> : <PublicLanding />;
  }

  // R1 (single-product mode): the standalone hub Overview at `/` is deferred
  // until a second product ships — a cross-product overview has nothing to
  // aggregate while one product is live. Send signed-in users straight to the
  // sole product's in-hub view. Restores automatically once a 2nd product goes
  // live (IS_SINGLE_PRODUCT_MODE flips false — see config/products.ts).
  if (IS_SINGLE_PRODUCT_MODE) {
    const solePath = SOLE_PRODUCT ? APP_DASHBOARD_PATHS[SOLE_PRODUCT.id] : undefined;
    if (solePath) return <Navigate to={solePath} replace />;
  }

  return <AuthenticatedDashboard view="home" user={user} userLabel={userLabel} logout={logout} />;
}

// ═════════════════════════════════════════════════════════════════════════════
// QR DASHBOARD ROUTE — /qr (the QR-specific dashboard, split out of "/")
// ═════════════════════════════════════════════════════════════════════════════
export function QrDashboard() {
  const { isAuthed, isAuthReady, user, userLabel, logout } = useAuth();
  const { t } = useTranslation();

  if (!isAuthReady) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <LogoMark color={T.purple} size={40} />
          <p style={{ fontSize: 14, color: T.textMuted, marginTop: 16 }}>{t("dashboard.common.loading")}</p>
        </div>
      </div>
    );
  }

  // The QR dashboard is authenticated-only; send signed-out visitors to the
  // public landing at "/" rather than rendering an empty shell.
  if (!isAuthed) {
    return <Navigate to="/" replace />;
  }

  return <AuthenticatedDashboard view="qr" user={user} userLabel={userLabel} logout={logout} />;
}

// ═════════════════════════════════════════════════════════════════════════════
// PLANNER DASHBOARD ROUTE — /planner (lightweight overview, no new data)
// ═════════════════════════════════════════════════════════════════════════════
export function PlannerDashboard() {
  const { isAuthed, isAuthReady, user, userLabel, logout } = useAuth();
  const { t } = useTranslation();

  if (!isAuthReady) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <LogoMark color={T.purple} size={40} />
          <p style={{ fontSize: 14, color: T.textMuted, marginTop: 16 }}>{t("dashboard.common.loading")}</p>
        </div>
      </div>
    );
  }

  if (!isAuthed) {
    return <Navigate to="/" replace />;
  }

  return <AuthenticatedDashboard view="planner" user={user} userLabel={userLabel} logout={logout} />;
}

// ═════════════════════════════════════════════════════════════════════════════
// AUTHENTICATED DASHBOARD — Command Deck v3
// ═════════════════════════════════════════════════════════════════════════════
function AuthenticatedDashboard({ view, user, userLabel, logout }: { view: "home" | "qr" | "planner"; user: any; userLabel: string; logout: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isHomeView = view === "home";
  const isQrView = view === "qr";
  const isPlannerView = view === "planner";

  // ADR-0015: SSO handoff for cross-app CTAs. Plain left-click mints a
  // one-time code and lands in the target app already signed in; modifier
  // clicks (cmd/ctrl/middle) keep default anchor behavior via the real href
  // (the target then shows its login — same as before the broker).
  const { accessToken } = useAuth();
  const hubApiBase = import.meta.env.VITE_API_BASE_URL as string;
  const openWithHandoff = (
    e: React.MouseEvent<HTMLAnchorElement>,
    targetProductId: string,
    targetUrl: string | undefined,
    to?: string,
  ) => {
    if (shouldBypassHandoff(e) || !targetUrl || !accessToken || !hubApiBase) return;
    e.preventDefault();
    void navigateWithHandoff({
      apiBase: hubApiBase,
      token: accessToken,
      targetProductId,
      targetUrl,
      to,
    });
  };
  const qrApp = APPS.find((a) => a.id === "qr");

  // HD-98: pull the user's real plan/billing state. Provider is already
  // mounted in App.tsx, so this is a free call (no extra HTTP — catalog is
  // cached cross-tab via PricingProvider's catalogCache).
  const pricing = usePricing();
  const { catalog, subscription } = pricing;
  const planView = useMemo(
    () => derivePlanView(catalog, subscription),
    [catalog, subscription],
  );
  // QR is the live product; the plan card reflects the real QR plan and its
  // upgrade lands in QR-scoped billing (not the all-products list).
  const qrPlan = useMemo(() => {
    const tier = subscription?.entitlements?.qr?.tier ?? "free";
    const proMonthly =
      catalog?.products.find((p) => p.productId === "qr")?.tiers?.pro?.monthlyPriceCents ?? null;
    return { tier, proMonthly };
  }, [catalog, subscription]);
  const [qrModal, setQrModal] = useState<{ intent: PlanChangeIntent; initialTier?: "pro" | "business" } | null>(null);

  // HD-97 M4: the legacy hardcoded "menu-spring-24" QR spotlight has
  // been extracted to <DashboardQrSpotlight />. The card now fetches the
  // user's real most-scanned QR from the QR app's M3 API and owns its
  // own copy-toast state — the parent no longer needs `copied`.

  const today = new Date();
  const dayName = today.toLocaleDateString("en-US", { weekday: "long" });
  const dateStr = today.toLocaleDateString("en-US", { day: "numeric", month: "long" });

  return (
    <div style={{ fontFamily: T.font, background: T.bg, minHeight: "100vh", color: T.textStrong, display: "flex" }}>
      <style>{`
        @keyframes hub-pulse { 0%,100%{box-shadow:0 0 0 0 rgba(34,197,94,0.45);} 50%{box-shadow:0 0 0 5px rgba(34,197,94,0);} }
        .hub-dot { animation: hub-pulse 2.4s ease-in-out infinite; }
        .hub-nav-item { transition: background 180ms, color 180ms; cursor: pointer; }
        .hub-nav-item:hover:not(.active) { background: ${T.mutedSoft}; color: ${T.navy}; }
        .hub-app-card { transition: transform 180ms cubic-bezier(0.16,1,0.3,1), box-shadow 180ms, border-color 180ms; }
        .hub-app-card-live:hover { transform: translateY(-2px); box-shadow: 0 16px 40px rgba(53,29,189,0.1); border-color: transparent; }
      `}</style>

      {/* ═════ LEFT RAIL ════════════════════════════════════════════ */}
      <aside
        style={{
          width: 240,
          flexShrink: 0,
          background: T.white,
          borderRight: `1px solid ${T.muted}`,
          minHeight: "100vh",
          padding: "24px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 24,
          position: "sticky",
          top: 0,
          alignSelf: "flex-start",
        }}
      >
        {/* Brand */}
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 8px", textDecoration: "none" }}>
          <LogoMark color={T.purple} size={22} />
          <span style={{ fontSize: 15, fontWeight: 800, color: T.navy, letterSpacing: "-0.02em" }}>{t("dashboard.home.brand")}</span>
        </Link>

        {/* Workspace switcher */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 12px",
            background: T.bgTint,
            border: `1px solid ${T.muted}`,
            borderRadius: 10,
            textAlign: "left",
          }}
        >
          <div style={{ width: 28, height: 28, borderRadius: 8, background: T.purple, color: T.white, display: "grid", placeItems: "center", fontSize: 12, fontWeight: 800 }}>
            {userLabel}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.navy, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {user?.name || t("dashboard.home.myWorkspace")}
            </div>
            <div style={{ fontSize: 11, color: T.textFaint }}>
              {/* Seat count hidden until the seats/invite feature ships —
                  it is always "1 seat" today, which reads as a dead placeholder. */}
              {planView ? (
                `${planView.planLabel} plan`
              ) : (
                <PlanSkeleton width={110} height={11} ariaLabel={t("dashboard.home.loadingPlanInfo")} />
              )}
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Overview (`/`) is deferred in single-product mode — it returns
              automatically once a 2nd product is live (see config/products.ts). */}
          {!IS_SINGLE_PRODUCT_MODE && (
            <>
              <NavSectionLabel>{t("dashboard.nav.overview")}</NavSectionLabel>
              <Link to="/" style={{ textDecoration: "none" }}>
                <NavItem icon="home" label={t("dashboard.nav.dashboard")} active={isHomeView} />
              </Link>
            </>
          )}

          <NavSectionLabel style={{ marginTop: 16 }}>{t("dashboard.nav.yourApps")}</NavSectionLabel>
          {/* Single-product mode: show only live apps (no coming-soon rail). */}
          {APPS.filter((a) => !IS_SINGLE_PRODUCT_MODE || a.status === "live").map((a) => {
            // Live products with an in-hub dashboard open it; others fall back
            // to their external app URL. QR + Planner live here (not Overview).
            const dashPath = APP_DASHBOARD_PATHS[a.id];
            const active =
              (a.id === "qr" && isQrView) || (a.id === "planner" && isPlannerView);
            return (
              <NavAppItem
                key={a.id}
                app={a}
                active={active}
                onClick={() => {
                  if (a.status !== "live") return;
                  if (dashPath) navigate(dashPath);
                  else if (a.url) window.location.href = a.url;
                }}
              />
            );
          })}
        </nav>

        <div style={{ flex: 1 }} />

        {/* Bottom */}
        <div style={{ display: "flex", flexDirection: "column", gap: 2, paddingTop: 16, borderTop: `1px solid ${T.muted}` }}>
          {/* Help & docs (docs site) and Settings (real settings page) are
              hidden until the next release — their destinations aren't ready
              yet. Re-add the two NavItems here when they ship. */}
          <button
            type="button"
            onClick={() => void logout()}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "9px 12px",
              borderRadius: 8,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 500,
              color: T.textFaint,
            }}
          >
            {t("dashboard.nav.signOut")}
          </button>
        </div>
      </aside>

      {/* ═════ MAIN ════════════════════════════════════════════════ */}
      <main style={{ flex: 1, minWidth: 0 }}>
        {/* Top bar removed: after HD-100 stripped search + notifications, its
            only content was a user chip that duplicated the sidebar workspace
            switcher (left). Identity lives in the left rail only. */}

        {/* Page content */}
        <div style={{ maxWidth: 1240, margin: "0 auto", padding: "40px 40px 64px" }}>
          {/* Page header */}
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24, flexWrap: "wrap", marginBottom: 32 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" as const, color: T.purple, marginBottom: 8 }}>
                {isQrView ? t("dashboard.qr.eyebrow") : isPlannerView ? t("dashboard.planner.eyebrow") : t("dashboard.home.eyebrow", { dayName, dateStr })}
              </div>
              <h1 style={{ fontSize: 30, fontWeight: 800, color: T.navy, margin: 0, letterSpacing: "-0.025em", lineHeight: 1.1 }}>
                {isQrView ? t("dashboard.qr.heading") : isPlannerView ? t("dashboard.planner.heading") : t("dashboard.home.heading")}
              </h1>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              {/* HD-100: Invite-teammate hidden by default. Set VITE_INVITE_ENABLED=true
                  on a paid-plan environment to surface it. Until a real seats backend
                  exists this is a placeholder mailto. */}
              {isHomeView && import.meta.env.VITE_INVITE_ENABLED === "true" && planView?.planLabel !== "Free" && (
                <a
                  href={`mailto:${import.meta.env.VITE_SUPPORT_EMAIL ?? "support@gen-pro-hub.com"}?subject=Invite%20teammate`}
                  style={{
                    padding: "10px 16px",
                    borderRadius: 10,
                    fontSize: 13,
                    fontWeight: 600,
                    background: T.white,
                    color: T.navy,
                    border: `1px solid ${T.muted}`,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    textDecoration: "none",
                  }}
                >
                  <Icon type="plus" color={T.navy} size={14} />
                  {t("dashboard.home.inviteTeammate")}
                </a>
              )}
              {isQrView && (
                <a
                  href={qrApp?.url}
                  onClick={(e) => openWithHandoff(e, "qr", qrApp?.url, "/designer")}
                  style={{
                    padding: "10px 18px",
                    borderRadius: 10,
                    fontSize: 13,
                    fontWeight: 600,
                    background: T.purple,
                    color: T.white,
                    border: "none",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    boxShadow: T.shadow.btn,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    textDecoration: "none",
                  }}
                >
                  <Icon type="plus" color={T.white} size={14} />
                  {t("dashboard.qr.newQrCode")}
                </a>
              )}
              {isPlannerView && (
                <a
                  href={APPS.find((a) => a.id === "planner")?.url}
                  style={{
                    padding: "10px 18px",
                    borderRadius: 10,
                    fontSize: 13,
                    fontWeight: 600,
                    background: T.purple,
                    color: T.white,
                    border: "none",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    boxShadow: T.shadow.btn,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    textDecoration: "none",
                  }}
                >
                  <Icon type="external" color={T.white} size={14} />
                  {t("dashboard.planner.openPlanner")}
                </a>
              )}
            </div>
          </div>

          {/* ADR-0010 M5c-3: cross-product invitation inbox. Renders nothing
              when there are no pending invites for the signed-in user.
              Home view only — a cross-product concern, not QR-specific. */}
          {/* Cross-product invitation inbox. Home view in ecosystem mode; in
              single-product mode the Overview is deferred, so it rides on the
              sole product's view (the landing) to stay reachable. */}
          {(isHomeView || IS_SINGLE_PRODUCT_MODE) && <PendingInvitations />}

          {/* QR dashboard widgets — KPI tiles (HD-95) + full-width live scan
              feed (HD-96). The most-scanned spotlight (HD-97) was removed
              (unfinished) — re-add it here when it's ready. */}
          {isQrView && (
            <>
              <DashboardKpiSection />
              <section style={{ marginBottom: 32 }}>
                <DashboardLiveFeed limit={20} />
              </section>
            </>
          )}

          {/* Planner dashboard — lightweight overview (no new data). Surfaces
              the Hub-known Planner plan + a deep link into the Planner app. */}
          {isPlannerView && (
            <section style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 20 }}>
              {/* Open Planner hero */}
              <div style={{ background: T.white, border: `1px solid ${T.muted}`, borderRadius: 20, padding: 28, boxShadow: T.shadow.sm, display: "flex", flexDirection: "column" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.purple, textTransform: "uppercase" as const, letterSpacing: "0.12em", marginBottom: 8 }}>{t("dashboard.planner.plannerLabel")}</div>
                <h3 style={{ fontSize: 20, fontWeight: 800, color: T.navy, margin: 0, letterSpacing: "-0.02em" }}>{t("dashboard.planner.heroTitle")}</h3>
                <p style={{ fontSize: 14, color: T.textMuted, margin: "8px 0 20px", lineHeight: 1.55, maxWidth: 520 }}>
                  {t("dashboard.planner.heroBody")}
                </p>
                <div style={{ marginTop: "auto" }}>
                  <a
                    href={APPS.find((a) => a.id === "planner")?.url}
                    style={{ padding: "12px 18px", borderRadius: 10, fontSize: 14, fontWeight: 700, background: T.purple, color: T.white, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, boxShadow: T.shadow.btn }}
                  >
                    {t("dashboard.planner.openPlanner")} <Icon type="external" color={T.white} size={14} />
                  </a>
                </div>
              </div>

              {/* Planner plan card */}
              <div style={{ background: T.white, border: `1px solid ${T.muted}`, borderRadius: 20, padding: 28, boxShadow: T.shadow.sm, display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.purple, textTransform: "uppercase" as const, letterSpacing: "0.12em" }}>{t("dashboard.planner.plannerPlanLabel")}</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: T.navy, textTransform: "capitalize" as const }}>
                  {subscription?.entitlements?.planner?.tier ?? "free"}
                </div>
                <p style={{ fontSize: 13, color: T.textMuted, margin: 0, lineHeight: 1.5 }}>
                  {t("dashboard.planner.manageBody")}
                </p>
                <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
                  <Link
                    to="/account/billing?product=planner"
                    style={{ padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600, background: T.bgTint, color: T.navy, border: `1px solid ${T.muted}`, textDecoration: "none", textAlign: "center" }}
                  >
                    {t("dashboard.planner.managePlan")}
                  </Link>
                </div>
              </div>
            </section>
          )}

          {/* Generic, product-agnostic dashboard body — home view only. */}
          {isHomeView && (
          <>
          {/* Hub progress + Account */}
          <section style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 20 }}>
            {/* Hub progress (ecosystem mode) / single-product plan panel */}
            <div style={{ background: T.white, border: `1px solid ${T.muted}`, borderRadius: 20, padding: 28, boxShadow: T.shadow.sm }}>
              {IS_SINGLE_PRODUCT_MODE ? (
                <SingleProductHubPanel />
              ) : (
              <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 4 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.purple, textTransform: "uppercase" as const, letterSpacing: "0.12em", marginBottom: 8 }}>{t("dashboard.home.yourHub")}</div>
                  <h3 style={{ fontSize: 20, fontWeight: 800, color: T.navy, margin: 0, letterSpacing: "-0.02em" }}>
                    {planView
                      ? t("dashboard.home.toolsUnlocked", { unlockedCount: planView.unlockedCount, totalProductCount: planView.totalProductCount })
                      : <PlanSkeleton width={220} height={24} ariaLabel={t("dashboard.home.loadingHubProgress")} />}
                  </h3>
                </div>
                {planView?.bundleSavingsEur != null && planView.bundleSavingsEur > 0 && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 999, background: T.orangeLight, color: T.orange, fontSize: 11, fontWeight: 700, letterSpacing: "0.04em" }}>
                    <Icon type="trend" color={T.orange} size={12} />
                    {t("dashboard.home.bundleSavings", { amount: planView.bundleSavingsEur })}
                  </span>
                )}
              </div>
              <p style={{ fontSize: 14, color: T.textMuted, margin: "8px 0 20px", lineHeight: 1.55, maxWidth: 520 }}>
                {planView
                  ? (planView.unlockedCount === 0
                      ? t("dashboard.home.allFree")
                      : planView.unlockedCount >= planView.totalProductCount
                        ? t("dashboard.home.allUnlocked")
                        : t("dashboard.home.someUnlocked", { unlockedCount: planView.unlockedCount, totalProductCount: planView.totalProductCount }))
                  : t("dashboard.home.loadingHub")}
              </p>

              {/* Progress bar */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 6, marginBottom: 20 }}>
                {APPS.map((a) => (
                  <div key={a.id} style={{ height: 6, borderRadius: 3, background: a.status === "live" ? a.accent : T.muted }} />
                ))}
              </div>

              {/* App timeline */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                {APPS.map((a) => {
                  const isLive = a.status === "live";
                  return (
                    <div
                      key={a.id}
                      className={`hub-app-card ${isLive ? "hub-app-card-live" : ""}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 12px",
                        border: `1px solid ${T.muted}`,
                        borderRadius: 12,
                        background: isLive ? T.white : T.bgTint,
                        cursor: isLive ? "pointer" : "default",
                        opacity: isLive ? 1 : 0.88,
                      }}
                      onClick={() => isLive && a.url && (window.location.href = a.url)}
                    >
                      <div style={{ width: 30, height: 30, borderRadius: 8, background: a.tint, color: a.accent, display: "grid", placeItems: "center", flexShrink: 0 }}>
                        <ProductIcon type={a.icon} color={a.accent} size={15} />
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: T.navy, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.name}</div>
                        <div style={{ fontSize: 10.5, color: isLive ? T.successText : T.textFaint, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" as const }}>
                          {isLive ? t("dashboard.home.statusLive") : t("dashboard.home.statusComingSoon")}
                        </div>
                      </div>
                      {isLive ? (
                        <Icon type="arrow" color={a.accent} size={14} />
                      ) : (
                        <Icon type="lock" color={T.textFaint} size={13} />
                      )}
                    </div>
                  );
                })}
              </div>
              </>
              )}
            </div>

            {/* Account card (dark) */}
            <div
              style={{
                background: `linear-gradient(165deg, ${T.navy} 0%, ${T.purpleDark} 100%)`,
                borderRadius: 20,
                padding: 28,
                color: T.white,
                boxShadow: T.shadow.md,
                position: "relative",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Prism ornament echo */}
              <div aria-hidden="true" style={{ position: "absolute", right: -24, top: -20, width: 140, height: 140, opacity: 0.4, pointerEvents: "none" }}>
                <div style={{ position: "absolute", width: 56, height: 72, borderRadius: 16, background: "rgba(255,255,255,0.08)", transform: "rotate(-6deg)", top: 18, left: 8, border: "1px solid rgba(255,255,255,0.2)" }} />
                <div style={{ position: "absolute", width: 60, height: 78, borderRadius: 16, background: "linear-gradient(145deg,#349DFF,#3D1DBD)", transform: "rotate(4deg)", top: 28, left: 52 }} />
                <div style={{ position: "absolute", width: 52, height: 66, borderRadius: 16, background: T.orange, transform: "rotate(-2deg)", top: 52, left: 32 }} />
              </div>

              <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: 18, flex: 1 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" as const, color: "rgba(255,255,255,0.65)", marginBottom: 10 }}>{t("dashboard.home.planLabel")}</div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                    {planView ? (
                      <>
                        <h3 style={{ fontSize: 28, fontWeight: 800, margin: 0, letterSpacing: "-0.02em" }}>
                          {planView.planLabel}
                        </h3>
                        <span style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>
                          {t("dashboard.home.seatSeeLimits", { seatLabel: planView.seatLabel })}
                        </span>
                      </>
                    ) : (
                      <PlanSkeleton width={140} height={28} dark ariaLabel={t("dashboard.home.loadingPlanName")} />
                    )}
                  </div>
                </div>

                <div style={{ height: 1, background: "rgba(255,255,255,0.12)" }} />

                <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                  {(planView ? [t("dashboard.home.toolsOnPaid", { unlockedCount: planView.unlockedCount, totalProductCount: planView.totalProductCount }), planView.planLabel === "Free" ? t("dashboard.home.freeTier") : t("dashboard.home.featuresUnlocked", { planLabel: planView.planLabel }), t("dashboard.home.manageBilling")] : []).map((text, i) => (
                    <li key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13.5, color: "rgba(255,255,255,0.88)", lineHeight: 1.4 }}>
                      <span style={{ width: 18, height: 18, borderRadius: "50%", background: "rgba(255,255,255,0.12)", display: "grid", placeItems: "center", flexShrink: 0 }}>
                        <Icon type="check" color={T.white} size={11} strokeWidth={3} />
                      </span>
                      {text}
                    </li>
                  ))}
                </ul>

                <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
                  {planView && (() => {
                    const isFree = qrPlan.tier === "free" && qrPlan.proMonthly != null;
                    const cta: React.CSSProperties = {
                      padding: "12px 18px",
                      borderRadius: 10,
                      fontSize: 14,
                      fontWeight: 700,
                      background: T.orange,
                      color: T.white,
                      border: "none",
                      cursor: "pointer",
                      fontFamily: "inherit",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      boxShadow: "0 2px 8px rgba(255,134,29,0.3)",
                      textDecoration: "none",
                    };
                    return isFree ? (
                      <button type="button" onClick={() => setQrModal({ intent: { kind: "buy" }, initialTier: "pro" })} style={cta}>
                        {t("dashboard.home.upgradeToPro", { price: formatEuro(qrPlan.proMonthly!) })}
                        <Icon type="arrow" color={T.white} size={14} />
                      </button>
                    ) : (
                      <Link to="/account/billing?product=qr" style={cta}>
                        {t("dashboard.qr.managePlan")}
                        <Icon type="arrow" color={T.white} size={14} />
                      </Link>
                    );
                  })()}
                  <Link
                    to="/pricing"
                    style={{
                      padding: "10px 16px",
                      borderRadius: 10,
                      fontSize: 13,
                      fontWeight: 600,
                      background: "transparent",
                      color: "rgba(255,255,255,0.8)",
                      border: "1px solid rgba(255,255,255,0.2)",
                      textDecoration: "none",
                      textAlign: "center",
                    }}
                  >
                    {t("dashboard.home.comparePlans")}
                  </Link>
                  {qrModal && (
                    <InlinePlanModal
                      pricing={pricing}
                      productId="qr"
                      intent={qrModal.intent}
                      initialTier={qrModal.initialTier}
                      onClose={() => setQrModal(null)}
                    />
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Meta row */}
          <div
            style={{
              marginTop: 24,
              padding: "18px 24px",
              background: T.white,
              border: `1px solid ${T.muted}`,
              borderRadius: 14,
              display: "flex",
              alignItems: "center",
              gap: 24,
              flexWrap: "wrap",
              boxShadow: T.shadow.sm,
            }}
          >
            <MetaItem label={t("dashboard.home.regionLabel")} value={t("dashboard.home.regionValue")} />
            <MetaItem label={t("dashboard.home.emailLabel")} value={user?.email ?? "—"} />
            {/* HD-98: "Member since" removed — no real source today; the previous
                value showed today's date every session, which was misleading. */}
            <div style={{ flex: 1 }} />
            <Link to="/settings" style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}>
              {t("dashboard.home.accountSettings")} <Icon type="arrow" color={T.textMuted} size={12} />
            </Link>
          </div>
          </>
          )}
        </div>
      </main>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═════════════════════════════════════════════════════════════════════════════

// Single-product mode: the "Your hub" progress/showcase card is replaced by a
// focused panel for the sole live product — no coming-soon grid, no per-product
// progress bar, no "N of M tools" copy. Reverts automatically once a second
// product goes live (IS_SINGLE_PRODUCT_MODE, see config/products.ts).
function SingleProductHubPanel() {
  const { t } = useTranslation();
  const product = SOLE_PRODUCT;
  const name = product?.name ?? t("dashboard.home.yourProductFallback");
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.purple, textTransform: "uppercase" as const, letterSpacing: "0.12em", marginBottom: 8 }}>{t("dashboard.home.yourPlan")}</div>
      <h3 style={{ fontSize: 20, fontWeight: 800, color: T.navy, margin: 0, letterSpacing: "-0.02em" }}>{name}</h3>
      <p style={{ fontSize: 14, color: T.textMuted, margin: "8px 0 20px", lineHeight: 1.55, maxWidth: 520 }}>
        {t("dashboard.home.manageProductBilling", { name })}
      </p>
      {product && (
        <Link
          to={`/account/billing?product=${product.id}`}
          style={{ display: "inline-block", padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600, background: T.bgTint, color: T.navy, border: `1px solid ${T.muted}`, textDecoration: "none" }}
        >
          {t("dashboard.home.manageProductPlan", { name })}
        </Link>
      )}
    </div>
  );
}

function NavSectionLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, color: T.textFaint, letterSpacing: "0.12em", textTransform: "uppercase" as const, padding: "0 12px", margin: "4px 0 8px", ...style }}>
      {children}
    </div>
  );
}

function NavItem({ icon, label, active, muted: isMuted }: { icon: string; label: string; active?: boolean; muted?: boolean }) {
  return (
    <div
      className={`hub-nav-item ${active ? "active" : ""}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "9px 12px",
        borderRadius: 8,
        background: active ? T.purpleLight : "transparent",
        color: active ? T.purple : isMuted ? T.textFaint : T.textStrong,
        fontSize: 13,
        fontWeight: active ? 700 : 500,
      }}
    >
      <Icon type={icon} color="currentColor" size={16} strokeWidth={active ? 2.4 : 2} />
      {label}
    </div>
  );
}

function NavAppItem({ app, active, onClick }: { app: AppInfo; active: boolean; onClick: () => void }) {
  const isLive = app.status === "live";
  return (
    <div
      className={`hub-nav-item ${active ? "active" : ""}`}
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px 8px 12px",
        borderRadius: 8,
        background: active ? T.purpleLight : "transparent",
        color: active ? T.purple : T.textStrong,
        fontSize: 13,
        fontWeight: active ? 700 : 500,
        opacity: isLive ? 1 : 0.65,
        cursor: isLive ? "pointer" : "default",
      }}
    >
      <div style={{ width: 22, height: 22, borderRadius: 6, background: app.tint, color: app.accent, display: "grid", placeItems: "center", flexShrink: 0 }}>
        <ProductIcon type={app.icon} color={app.accent} size={12} />
      </div>
      <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{app.name}</span>
      {isLive ? (
        <span className="hub-dot" style={{ width: 6, height: 6, borderRadius: "50%", background: T.success }} />
      ) : (
        <Icon type="lock" color={T.textFaint} size={11} />
      )}
    </div>
  );
}


// HD-97 M4: <Field> removed — it was used only by the inline QR spotlight
// that <DashboardQrSpotlight /> now replaces. If a future widget needs a
// labelled value chip, re-introduce it then.

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: T.textFaint, letterSpacing: "0.1em", textTransform: "uppercase" as const, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.navy }}>{value}</div>
    </div>
  );
}

/**
 * HD-98: Skeleton placeholder used while `usePricing()` data is in flight.
 * Sized explicitly by the caller to reserve layout space and avoid CLS
 * when the real text replaces it. Use `dark` on dark backgrounds (account card).
 */
function PlanSkeleton({
  width,
  height,
  dark,
  ariaLabel,
}: {
  width: number;
  height: number;
  dark?: boolean;
  ariaLabel: string;
}) {
  return (
    <span
      role="status"
      aria-label={ariaLabel}
      style={{
        display: "inline-block",
        width,
        height,
        borderRadius: 4,
        background: dark ? "rgba(255,255,255,0.18)" : T.mutedSoft,
        verticalAlign: "middle",
      }}
    />
  );
}

// HD-97 M4: <QrPattern> removed — it was the SVG mockup behind the
// hardcoded "menu-spring-24" placeholder, now superseded by the real
// thumbnail rendered inside <DashboardQrSpotlight /> via <QrThumbnail>.
